/**
 * Surface the REAL cause of a `supabase.functions.invoke` failure.
 *
 * On a non-2xx status supabase-js hands back a `FunctionsHttpError` whose
 * `.message` is the generic "Edge Function returned a non-2xx status code" — the
 * actionable body (`{ error, message }` our functions return, or a gateway 404
 * when the function was never deployed) lives on `error.context`, the raw
 * `Response`. Structurally reading it (no transitive class import) turns
 * "Something went wrong" into "router_error: OpenAI … model not found" or
 * "Edge function error (404)", which is what the caller actually needs to act.
 *
 * Lives in `shared/lib` rather than in a feature slice because more than one
 * slice needs it and CLAUDE.md forbids importing across `features/<a>` →
 * `features/<b>`. It was in `features/institute/builder-api.ts` until Phase 183
 * needed it from the admin slice.
 */
export async function edgeErrorMessage(error: unknown): Promise<string> {
  if (error !== null && typeof error === 'object' && 'context' in error) {
    const ctx = (error as { context?: unknown }).context;
    if (ctx instanceof Response) {
      try {
        const body = (await ctx.clone().json()) as Record<string, unknown>;
        const msg = typeof body['message'] === 'string' ? body['message'] : undefined;
        const code = typeof body['error'] === 'string' ? body['error'] : undefined;
        if (msg !== undefined && code !== undefined) return `${code}: ${msg}`;
        return msg ?? code ?? `Edge function error (${String(ctx.status)})`;
      } catch {
        return `Edge function error (${String(ctx.status)})`;
      }
    }
  }
  return error instanceof Error && error.message !== '' ? error.message : 'Unknown error';
}
